import type { InferRouterOutputs } from '@orpc/server';
import type { AppRouter } from '@server/rpc';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { KeyRound, MoreVertical, RefreshCw, ShieldCheck, Trash2, User, UserPlus, X } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { SearchInput } from '@/_shared/components/SearchInput';
import { Table, type TableColumn } from '@/_shared/components/Table';
import { Avatar, AvatarFallback, AvatarImage } from '@/_shared/components/ui/avatar';
import { Badge } from '@/_shared/components/ui/badge';
import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/_shared/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/_shared/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/_shared/components/ui/tooltip';
import type { WorkspaceRole } from '@/_shared/contexts/WorkspaceContext';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useAuth } from '@/_shared/hooks/useAuth';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { useRudderstackSignInAgain } from '@/_shared/hooks/useRudderstackSignInAgain';

import { GatedControl } from './GatedControl';
import {
  type InviteCandidate,
  InviteOrganizationMemberDialog,
  type LookoutRole,
} from './InviteOrganizationMemberDialog';
import { MemberPermissionsDrawer, type PermissionsTarget } from './MemberPermissionsDrawer';
import { RemoveSiblingMemberDialog, type RemoveTarget } from './RemoveSiblingMemberDialog';
import { SiblingInviteDialog } from './SiblingInviteDialog';
import { StepUpPasswordDialog } from './StepUpPasswordDialog';

type Outputs = InferRouterOutputs<AppRouter>;
export type SiblingOverview = Extract<Outputs['siblingMembers']['overview'], { model: 'sibling' }>;
type LookoutMember = Outputs['workspace']['get']['members'][number];
type OrganizationMember = SiblingOverview['organizationMembers'][number];
type Invitation = SiblingOverview['invitations'][number];
type RudderstackRole = SiblingOverview['rudderstackRoles'][string];

const SECTION_HEADING = 'text-sm font-bold tracking-wider text-muted-foreground uppercase';
const NOT_RS_ADMIN = {
  invite: 'Only a RudderStack admin of the organization can invite people to this workspace.',
  remove: 'Only a RudderStack admin of the organization can remove people from this workspace.',
  addAgain: 'Only a RudderStack admin of the organization can add people again.',
  invitations: 'Only a RudderStack admin of the organization can resend or cancel invitations.',
  organization: 'Only a RudderStack admin of the organization can see the people of the organization.',
  role: 'Only a RudderStack admin of the organization can change roles in this workspace.',
  permissions: 'Only a RudderStack admin of the organization can see the permissions of other people.',
};
/** A new Lookout admin gets the three permissions; when RudderStack did not save them, the admin can do it. */
const ADMIN_GRANTS_FAILED = 'Lookout could not give the RudderStack permissions. Turn them on in Permissions.';
const RESEND_AFTER_EXPIRY = 'RudderStack resends an invitation only after it expires.';
const PASSWORD_REFUSED = 'The password is not correct. Try again.';
const ROLE_UNKNOWN = "Lookout cannot read this person's RudderStack role right now.";

const RUDDERSTACK_ROLE_BADGES: Record<RudderstackRole, { label: string; variant: 'highlight' | 'info' | 'warning' }> = {
  admin: { label: 'admin', variant: 'highlight' },
  member: { label: 'member', variant: 'info' },
  suspended: { label: 'suspended', variant: 'warning' },
  removed: { label: 'removed', variant: 'warning' },
};

type StepUpAction =
  | { kind: 'invite'; email: string; role: LookoutRole }
  | { kind: 'resend'; inviteId: string; email: string }
  | { kind: 'cancel'; inviteId: string; email: string };
interface StepUpState {
  action: StepUpAction;
  scheme: string;
  authorizationToken: string;
  error: string | null;
}
interface StepUpAnswer {
  scheme: string;
  authorizationToken: string;
  password: string;
}

interface RoleChange {
  userId: string;
  name: string;
  role: 'admin' | 'member';
}

function roleBadgeVariant(role: WorkspaceRole): 'highlight' | 'warning' | 'info' {
  if (role === 'owner') return 'highlight';
  if (role === 'admin') return 'warning';
  return 'info';
}

function memberName(member: LookoutMember): string {
  return member.user.displayName ?? member.user.githubLogin ?? 'user';
}

/** Whether any of the texts holds the search, without case. An empty search matches everything. */
function matchesSearch(search: string, texts: readonly (string | null | undefined)[]): boolean {
  const query = search.trim().toLowerCase();
  return query === '' || texts.some((text) => (text ?? '').toLowerCase().includes(query));
}

/** The empty text of a table: no match for a search, else the table's own text. */
function emptyText(search: string, text: string): string {
  return search.trim() ? 'No matches.' : text;
}

/** Why a member shows "Removed" or "Suspended": the date of the membership check's mark, when it has one. */
function rudderstackRoleReason(role: RudderstackRole, missingSince: string | null): string | null {
  if (role === 'suspended') return 'The RudderStack organization suspended this person.';
  if (role !== 'removed') return null;
  const since = missingSince ? ` Lookout noticed it on ${dayjs(missingSince).format('MMM D, YYYY')}.` : '';
  return `This person is no longer in this workspace in RudderStack.${since}`;
}

function RudderstackRoleBadge({
  role,
  missingSince,
}: {
  role: RudderstackRole | undefined;
  missingSince: string | null;
}): React.ReactElement {
  if (role === undefined) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-sm text-muted-foreground" aria-label={ROLE_UNKNOWN}>
            —
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">{ROLE_UNKNOWN}</TooltipContent>
      </Tooltip>
    );
  }
  const { label, variant } = RUDDERSTACK_ROLE_BADGES[role];
  const badge = (
    <Badge variant={variant} className="tracking-wider uppercase">
      {label}
    </Badge>
  );
  const reason = rudderstackRoleReason(role, missingSince);
  if (reason === null) return badge;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0} className="inline-flex rounded-sm focus-ring">
          {badge}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{reason}</TooltipContent>
    </Tooltip>
  );
}

function stepUpLabel(action: StepUpAction): string {
  if (action.kind === 'invite') return `invite ${action.email}`;
  if (action.kind === 'resend') return `resend the invitation to ${action.email}`;
  return `cancel the invitation to ${action.email}`;
}

/**
 * The Users tab of a sibling workspace (ACT2-1124 LLD §5.2–§5.7), in three tables, each with a search box:
 *
 * - "In this workspace": the Lookout members, with their Lookout role and RudderStack role. "Removed" is a person
 *   whom the web app removed from the organization or the sibling. For an RS admin, a row opens the person's
 *   permissions. Anyone else opens only their own, read-only, from the row menu.
 * - "In RudderStack organization": the other active organization members, who are not in this workspace, with
 *   Invite. Only RudderStack admins see them.
 * - "Invites": RudderStack invitations and Lookout invites.
 *
 * Invite, resend, cancel, Add again and Remove change RudderStack or need its role, so only RudderStack admins can use
 * them. Others see them disabled with the reason.
 */
export function SiblingMembers({
  members,
  overview,
}: {
  members: LookoutMember[];
  overview: SiblingOverview;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const orpc = useOrpc();
  // ACT2-1124: an ended RudderStack sign-in offers "Sign in again" on the toast.
  const signIn = useRudderstackSignInAgain();
  const { user } = useAuth();
  const { isAdmin } = useWorkspace();
  const rsAdmin = overview.viewerIsRsAdmin;
  const roles = overview.rudderstackRoles;

  const [inviteOpen, setInviteOpen] = useState(false);
  const [stepUp, setStepUp] = useState<StepUpState | null>(null);
  const [inviteCandidate, setInviteCandidate] = useState<{
    candidate: InviteCandidate;
    fromInvite: boolean;
    role: LookoutRole;
  } | null>(null);
  const [permissionsTarget, setPermissionsTarget] = useState<PermissionsTarget | null>(null);
  const [removeTarget, setRemoveTarget] = useState<RemoveTarget | null>(null);
  const [cancelTarget, setCancelTarget] = useState<Invitation | null>(null);
  const [roleChange, setRoleChange] = useState<RoleChange | null>(null);
  const [memberSearch, setMemberSearch] = useState('');
  const [organizationSearch, setOrganizationSearch] = useState('');
  const [invitationSearch, setInvitationSearch] = useState('');

  const shownMembers = useMemo(
    () =>
      members.filter((member) =>
        matchesSearch(memberSearch, [member.user.displayName, member.user.githubLogin, member.user.email]),
      ),
    [members, memberSearch],
  );
  const shownOrganizationMembers = useMemo(
    () =>
      overview.organizationMembers.filter((person) => matchesSearch(organizationSearch, [person.name, person.email])),
    [overview.organizationMembers, organizationSearch],
  );
  const shownInvitations = useMemo(
    () => overview.invitations.filter((invitation) => matchesSearch(invitationSearch, [invitation.email])),
    [overview.invitations, invitationSearch],
  );

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: orpc.siblingMembers.overview.queryOptions({ input: {} }).queryKey });
    void queryClient.invalidateQueries({ queryKey: orpc.workspace.get.queryOptions({ input: {} }).queryKey });
  };

  /** A step-up answer opens (or keeps) the password dialog; anything else closes it. */
  const handleStepUp = (
    action: StepUpAction,
    data: { kind: string; scheme?: string; authorizationToken?: string },
  ): boolean => {
    if (data.kind !== 'step_up') {
      setStepUp(null);
      return false;
    }
    setStepUp({
      action,
      scheme: data.scheme ?? 'password',
      authorizationToken: data.authorizationToken ?? '',
      error: null,
    });
    return true;
  };

  const onStepUpError = (err: Error, fallback: string): void => {
    if (stepUp && err.message === PASSWORD_REFUSED) {
      setStepUp({ ...stepUp, error: err.message });
      return;
    }
    setStepUp(null);
    if (signIn.toastIfReauth(err)) return;
    toast.error(err.message || fallback);
  };

  const inviteMutation = useMutation(
    orpc.siblingMembers.invite.mutationOptions({
      onSuccess: (data, variables) => {
        setInviteOpen(false);
        if (handleStepUp({ kind: 'invite', email: variables.email, role: variables.role }, data)) return;
        if (data.kind === 'in_organization') {
          setInviteCandidate({ candidate: data.member, fromInvite: true, role: variables.role });
          return;
        }
        toast.success('Invitation sent');
        refresh();
      },
      onError: (err) => onStepUpError(err, 'Failed to send invitation'),
    }),
  );

  const resendMutation = useMutation(
    orpc.siblingMembers.resendInvitation.mutationOptions({
      onSuccess: (data, variables) => {
        const invitation = overview.invitations.find((i) => i.id === variables.inviteId);
        if (handleStepUp({ kind: 'resend', inviteId: variables.inviteId, email: invitation?.email ?? '' }, data))
          return;
        toast.success('Invitation resent');
        refresh();
      },
      onError: (err) => onStepUpError(err, 'Failed to resend invitation'),
    }),
  );

  const cancelMutation = useMutation(
    orpc.siblingMembers.cancelInvitation.mutationOptions({
      onSuccess: (data, variables) => {
        setCancelTarget(null);
        const invitation = overview.invitations.find((i) => i.id === variables.inviteId);
        if (handleStepUp({ kind: 'cancel', inviteId: variables.inviteId, email: invitation?.email ?? '' }, data))
          return;
        toast.success('Invitation cancelled');
        refresh();
      },
      onError: (err) => {
        setCancelTarget(null);
        onStepUpError(err, 'Failed to cancel invitation');
      },
    }),
  );

  const inviteMemberMutation = useMutation(
    orpc.siblingMembers.inviteOrganizationMember.mutationOptions({
      onSuccess: (data) => {
        setInviteCandidate(null);
        if (data.adminGrants === 'failed') toast.warning(`Invitation sent. ${ADMIN_GRANTS_FAILED}`);
        else toast.success('Invitation sent');
        refresh();
      },
      onError: (err) => {
        if (!signIn.toastIfReauth(err)) toast.error(err.message || 'Failed to send invitation');
      },
    }),
  );

  const addAgainMutation = useMutation(
    orpc.siblingMembers.addAgain.mutationOptions({
      onSuccess: () => {
        toast.success('Added again in RudderStack');
        refresh();
      },
      onError: (err) => {
        if (!signIn.toastIfReauth(err)) toast.error(err.message || 'Failed to add again');
      },
    }),
  );

  const removeMutation = useMutation(
    orpc.siblingMembers.remove.mutationOptions({
      onSuccess: () => {
        setRemoveTarget(null);
        toast.success('Member removed');
        refresh();
      },
      onError: (err) => {
        if (!signIn.toastIfReauth(err)) toast.error(err.message || 'Failed to remove member');
      },
    }),
  );

  const roleMutation = useMutation(
    orpc.siblingMembers.changeRole.mutationOptions({
      onSuccess: (data) => {
        setRoleChange(null);
        if (data.adminGrants === 'failed') toast.warning(`Role updated. ${ADMIN_GRANTS_FAILED}`);
        else toast.success('Role updated');
        refresh();
        // A new Lookout admin has new grant sets: the permissions drawer reads them again.
        void queryClient.invalidateQueries({ queryKey: orpc.siblingGrants.key() });
      },
      onError: (err) => {
        if (!signIn.toastIfReauth(err)) toast.error(err.message || 'Failed to update role');
      },
    }),
  );

  const submitStepUp = (password: string): void => {
    if (!stepUp) return;
    const answer: StepUpAnswer = { scheme: stepUp.scheme, authorizationToken: stepUp.authorizationToken, password };
    const { action } = stepUp;
    if (action.kind === 'invite') inviteMutation.mutate({ email: action.email, role: action.role, stepUp: answer });
    else if (action.kind === 'resend') resendMutation.mutate({ inviteId: action.inviteId, stepUp: answer });
    else cancelMutation.mutate({ inviteId: action.inviteId, stepUp: answer });
  };

  const openPermissions = useCallback(
    (member: LookoutMember): void => {
      setPermissionsTarget({
        userId: member.userId,
        name: memberName(member),
        email: member.user.email ?? null,
        rudderstackAdmin: roles[member.userId] === 'admin',
      });
    },
    [roles],
  );

  // The Table renders each `cell` as a component, so a new column list remounts every cell and closes an open row
  // menu. `useMutation` returns a new object on each render, so the columns take only its stable `mutate` and the
  // `isPending` flag.
  const { mutate: addAgain, isPending: addAgainPending } = addAgainMutation;
  const { mutate: resend, isPending: resendPending } = resendMutation;

  const memberColumns = useMemo<TableColumn<LookoutMember>[]>(
    () => [
      {
        id: 'user',
        header: 'User',
        sortable: false,
        activatesRow: true,
        activatesRowLabel: (member) => `Permissions of ${memberName(member)}`,
        cell: (member) => {
          const name = memberName(member);
          return (
            <div className="flex min-w-0 items-center gap-3">
              <Avatar className="size-8 shrink-0">
                <AvatarImage src={member.user.avatarUrl ?? member.user.githubAvatarUrl ?? undefined} alt={name} />
                <AvatarFallback>{name[0]?.toUpperCase()}</AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <div className="flex min-w-0 items-center gap-2">
                  <p className="truncate text-sm font-medium text-foreground">{name}</p>
                  {member.userId === user?.id ? (
                    <Badge variant="outline" className="shrink-0 tracking-wider uppercase">
                      You
                    </Badge>
                  ) : null}
                </div>
                {member.user.email ? (
                  <p className="truncate text-xs text-muted-foreground">{member.user.email}</p>
                ) : null}
              </div>
            </div>
          );
        },
      },
      {
        id: 'lookoutRole',
        header: 'Lookout role',
        width: '9rem',
        sortable: false,
        cell: (member) => (
          <Badge variant={roleBadgeVariant(member.role)} className="tracking-wider uppercase">
            {member.role}
          </Badge>
        ),
      },
      {
        id: 'rudderstackRole',
        header: 'RudderStack role',
        width: '10rem',
        sortable: false,
        cell: (member) => (
          <RudderstackRoleBadge role={roles[member.userId]} missingSince={member.rudderstackMissingSince ?? null} />
        ),
      },
      {
        id: 'actions',
        header: '',
        headerAriaLabel: 'Actions',
        align: 'right',
        width: '3.5rem',
        sortable: false,
        cell: (member) => {
          const name = memberName(member);
          const self = member.userId === user?.id;
          const owner = member.role === 'owner';
          const role = roles[member.userId];
          const canAddAgain = role === 'removed' || Boolean(member.rudderstackMissingSince);
          let roleReason: string | null = null;
          if (!rsAdmin) roleReason = NOT_RS_ADMIN.role;
          else if (self) roleReason = 'You cannot change your own role.';
          else if (owner) roleReason = 'The owner of the workspace keeps the owner role.';
          let removeReason: string | null = null;
          if (!rsAdmin) removeReason = NOT_RS_ADMIN.remove;
          else if (self) removeReason = 'You cannot remove yourself. Leave the workspace on the General tab.';
          else if (owner) removeReason = 'The owner of the workspace cannot be removed.';
          const makeAdmin = member.role === 'member';
          // Someone who is not an RS admin sees only their own permissions, read-only.
          const permissionsReason = !rsAdmin && !self ? NOT_RS_ADMIN.permissions : null;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="tertiary" iconOnly Icon={<MoreVertical />} aria-label={`Actions for ${name}`} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <MenuGate reason={permissionsReason}>
                  <DropdownMenuItem disabled={permissionsReason !== null} onSelect={() => openPermissions(member)}>
                    <KeyRound className="size-4" />
                    Permissions
                  </DropdownMenuItem>
                </MenuGate>
                <MenuGate reason={roleReason}>
                  <DropdownMenuItem
                    disabled={roleReason !== null}
                    onSelect={() =>
                      setRoleChange({ userId: member.userId, name, role: makeAdmin ? 'admin' : 'member' })
                    }
                  >
                    {makeAdmin ? <ShieldCheck className="size-4" /> : <User className="size-4" />}
                    {makeAdmin ? 'Make admin' : 'Make member'}
                  </DropdownMenuItem>
                </MenuGate>
                {canAddAgain ? (
                  <MenuGate reason={rsAdmin ? null : NOT_RS_ADMIN.addAgain}>
                    <DropdownMenuItem
                      disabled={!rsAdmin || addAgainPending}
                      onSelect={() => addAgain({ userId: member.userId })}
                    >
                      <UserPlus className="size-4" />
                      Add again
                    </DropdownMenuItem>
                  </MenuGate>
                ) : null}
                <MenuGate reason={removeReason}>
                  <DropdownMenuItem
                    variant="destructive"
                    disabled={removeReason !== null}
                    onSelect={() => setRemoveTarget({ userId: member.userId, name, rsAdmin: role === 'admin' })}
                  >
                    <Trash2 className="size-4" />
                    Remove
                  </DropdownMenuItem>
                </MenuGate>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [addAgain, addAgainPending, openPermissions, roles, rsAdmin, user?.id],
  );

  const organizationColumns = useMemo<TableColumn<OrganizationMember>[]>(
    () => [
      {
        id: 'user',
        header: 'User',
        sortable: false,
        cell: (person) => (
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-foreground">{person.name ?? person.email}</p>
            {person.name ? <p className="truncate text-xs text-muted-foreground">{person.email}</p> : null}
          </div>
        ),
      },
      {
        id: 'rudderstackRole',
        header: 'RudderStack role',
        width: '10rem',
        sortable: false,
        cell: (person) => <RudderstackRoleBadge role={person.organizationRole} missingSince={null} />,
      },
      {
        id: 'invite',
        header: '',
        headerAriaLabel: 'Invite',
        align: 'right',
        width: '8rem',
        sortable: false,
        cell: (person) =>
          person.invitePending ? (
            <Badge variant="success">Invited</Badge>
          ) : (
            <Button
              type="button"
              variant="secondary"
              size="md"
              Icon={<UserPlus />}
              aria-label={`Invite ${person.email}`}
              onClick={() => setInviteCandidate({ candidate: person, fromInvite: false, role: 'member' })}
              label="Invite"
            />
          ),
      },
    ],
    [],
  );

  const invitationColumns = useMemo<TableColumn<Invitation>[]>(
    () => [
      {
        id: 'email',
        header: 'Email',
        sortable: false,
        cell: (invitation) => (
          <span className="block truncate text-sm" title={invitation.email}>
            {invitation.email}
          </span>
        ),
      },
      {
        id: 'role',
        header: 'Role',
        width: '7rem',
        sortable: false,
        cell: (invitation) => (
          <Badge variant={roleBadgeVariant(invitation.role)} className="tracking-wider uppercase">
            {invitation.role}
          </Badge>
        ),
      },
      {
        id: 'lastSentAt',
        header: 'Sent',
        width: '8rem',
        sorter: 'timestamp',
        cell: (invitation) => dayjs(invitation.lastSentAt).format('MMM D, YYYY'),
      },
      {
        id: 'expiresAt',
        header: 'Expiry',
        width: '8rem',
        sorter: 'timestamp',
        cell: (invitation) =>
          invitation.acceptedInRudderstack ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0} className="inline-flex rounded-sm focus-ring">
                  <Badge variant="success">Accepted</Badge>
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                {`The invitation is accepted in RudderStack. ${invitation.email} gets the Lookout role at their next sign-in.`}
              </TooltipContent>
            </Tooltip>
          ) : invitation.expired ? (
            <Badge variant="warning">Expired</Badge>
          ) : (
            dayjs(invitation.expiresAt).format('MMM D, YYYY')
          ),
      },
      {
        id: 'actions',
        header: '',
        headerAriaLabel: 'Actions',
        align: 'right',
        width: '3.5rem',
        sortable: false,
        cell: (invitation) => {
          let resendReason: string | null = null;
          if (!rsAdmin) resendReason = NOT_RS_ADMIN.invitations;
          else if (invitation.kind === 'rudderstack' && !invitation.expired) resendReason = RESEND_AFTER_EXPIRY;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="tertiary"
                  iconOnly
                  Icon={<MoreVertical />}
                  aria-label={`Invite actions for ${invitation.email}`}
                />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <MenuGate reason={resendReason}>
                  <DropdownMenuItem
                    disabled={resendReason !== null || resendPending}
                    onSelect={() => resend({ inviteId: invitation.id })}
                  >
                    <RefreshCw className="size-4" />
                    Resend invitation
                  </DropdownMenuItem>
                </MenuGate>
                <MenuGate reason={rsAdmin ? null : NOT_RS_ADMIN.invitations}>
                  <DropdownMenuItem
                    variant="destructive"
                    disabled={!rsAdmin}
                    onSelect={() => setCancelTarget(invitation)}
                  >
                    <X className="size-4" />
                    Cancel invitation
                  </DropdownMenuItem>
                </MenuGate>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [rsAdmin, resend, resendPending],
  );

  const stepUpPending = inviteMutation.isPending || resendMutation.isPending || cancelMutation.isPending;

  return (
    <>
      {!overview.memberListAvailable ? (
        <Callout variant="warning" className="mb-6">
          Lookout cannot read the members of your RudderStack organization right now, so the RudderStack admin actions
          are off. Try again in a few minutes.
        </Callout>
      ) : null}

      <section className="mb-8" aria-labelledby="members-in-workspace">
        <div className="mb-4 flex items-center justify-between gap-4">
          <h2 id="members-in-workspace" className={SECTION_HEADING}>
            In this workspace
          </h2>
          {(members.length > 0 || memberSearch !== '') && (
            <SearchInput
              value={memberSearch}
              onChange={setMemberSearch}
              placeholder="Search members..."
              className="w-full max-w-xs shrink-0"
            />
          )}
        </div>
        <Table
          data={shownMembers}
          columns={memberColumns}
          getRowId={(member) => member.userId}
          onRowClick={rsAdmin ? openPermissions : undefined}
          fixedLayout
          maxHeight="24rem"
          emptyState={emptyText(memberSearch, 'No members')}
        />
      </section>

      <section className="mb-8" aria-labelledby="members-in-organization">
        <div className="mb-4 flex items-center justify-between gap-4">
          <h2 id="members-in-organization" className={SECTION_HEADING}>
            In RudderStack organization
          </h2>
          {rsAdmin && (overview.organizationMembers.length > 0 || organizationSearch !== '') && (
            <SearchInput
              value={organizationSearch}
              onChange={setOrganizationSearch}
              placeholder="Search the organization..."
              className="w-full max-w-xs shrink-0"
            />
          )}
        </div>
        {rsAdmin ? (
          <Table
            data={shownOrganizationMembers}
            columns={organizationColumns}
            getRowId={(person) => person.rsUserId}
            fixedLayout
            maxHeight="20rem"
            emptyState={emptyText(organizationSearch, 'Everyone in the organization is in this workspace')}
          />
        ) : (
          <Callout variant="info">{NOT_RS_ADMIN.organization}</Callout>
        )}
      </section>

      {isAdmin || rsAdmin ? (
        <section className="mb-8" aria-labelledby="members-invites">
          <div className="mb-4 flex items-center justify-between gap-4">
            <h2 id="members-invites" className={SECTION_HEADING}>
              Invites
            </h2>
            <div className="flex min-w-0 items-center gap-2 p-0.5">
              {(overview.invitations.length > 0 || invitationSearch !== '') && (
                <SearchInput
                  value={invitationSearch}
                  onChange={setInvitationSearch}
                  placeholder="Search invites..."
                  className="w-full max-w-xs"
                />
              )}
              <GatedControl reason={rsAdmin ? null : NOT_RS_ADMIN.invite}>
                <Button
                  variant="primary"
                  size="md"
                  Icon={<UserPlus />}
                  disabled={!rsAdmin}
                  onClick={() => setInviteOpen(true)}
                  label="Invite"
                />
              </GatedControl>
            </div>
          </div>
          <Table
            data={shownInvitations}
            columns={invitationColumns}
            getRowId={(invitation) => invitation.id}
            enableSort
            fixedLayout
            emptyState={emptyText(invitationSearch, 'No pending invitations')}
            maxHeight="20rem"
          />
        </section>
      ) : null}

      {inviteOpen ? (
        <SiblingInviteDialog
          open={inviteOpen}
          pending={inviteMutation.isPending}
          onSubmit={(input) => inviteMutation.mutate(input)}
          onClose={() => setInviteOpen(false)}
        />
      ) : null}

      <StepUpPasswordDialog
        key={stepUp ? stepUp.authorizationToken : 'closed'}
        action={stepUp ? stepUpLabel(stepUp.action) : null}
        scheme={stepUp?.scheme ?? null}
        pending={stepUpPending}
        error={stepUp?.error ?? null}
        onSubmit={submitStepUp}
        onClose={() => setStepUp(null)}
      />

      <InviteOrganizationMemberDialog
        key={inviteCandidate ? `${inviteCandidate.candidate.rsUserId}:${inviteCandidate.role}` : 'closed'}
        candidate={inviteCandidate?.candidate ?? null}
        fromInvite={inviteCandidate?.fromInvite ?? false}
        initialRole={inviteCandidate?.role ?? 'member'}
        pending={inviteMemberMutation.isPending}
        onConfirm={(input) => inviteMemberMutation.mutate(input)}
        onClose={() => setInviteCandidate(null)}
      />

      <MemberPermissionsDrawer
        target={permissionsTarget}
        viewerIsRsAdmin={rsAdmin}
        onClose={() => setPermissionsTarget(null)}
      />

      <RemoveSiblingMemberDialog
        key={removeTarget?.userId ?? 'closed'}
        target={removeTarget}
        pending={removeMutation.isPending}
        onConfirm={(input) => removeMutation.mutate(input)}
        onClose={() => setRemoveTarget(null)}
      />

      <Dialog
        open={cancelTarget !== null}
        onOpenChange={(open) => {
          if (!open && !cancelMutation.isPending) setCancelTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel invitation?</DialogTitle>
            <DialogDescription>
              {cancelTarget
                ? `${cancelTarget.kind === 'rudderstack' ? 'RudderStack' : 'Lookout'} cancels the invitation to ` +
                  `${cancelTarget.email}. Its link stops working.`
                : ''}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              disabled={cancelMutation.isPending}
              onClick={() => setCancelTarget(null)}
              label="Keep it"
            />
            <Button
              type="button"
              variant="primary-destructive"
              loading={cancelMutation.isPending}
              onClick={() => cancelTarget && cancelMutation.mutate({ inviteId: cancelTarget.id })}
              label="Cancel invitation"
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={roleChange !== null}
        onOpenChange={(open) => {
          if (!open && !roleMutation.isPending) setRoleChange(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{roleChange?.role === 'admin' ? 'Make admin' : 'Make member'}</DialogTitle>
            <DialogDescription>
              {roleChange?.role === 'admin'
                ? `${roleChange.name} will be able to manage settings, sources and members in Lookout.`
                : `${roleChange?.name ?? ''} will lose admin access to settings, sources and members in Lookout.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              disabled={roleMutation.isPending}
              onClick={() => setRoleChange(null)}
              label="Cancel"
            />
            <Button
              type="button"
              variant="primary"
              loading={roleMutation.isPending}
              onClick={() => roleChange && roleMutation.mutate({ userId: roleChange.userId, role: roleChange.role })}
              label={roleChange?.role === 'admin' ? 'Make admin' : 'Make member'}
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** A menu item that the person cannot use: a disabled item gets no pointer events, so a wrapper holds the tooltip. */
function MenuGate({ reason, children }: { reason: string | null; children: React.ReactElement }): React.ReactElement {
  if (reason === null) return children;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div>{children}</div>
      </TooltipTrigger>
      <TooltipContent side="left" className="max-w-xs">
        {reason}
      </TooltipContent>
    </Tooltip>
  );
}
